/*
 * Super Admin → Settings → Landing Page.
 * Toggle the public "Get Started" primary CTA between Standard commercial
 * onboarding and Founding Partner direct (no-payment) onboarding, with a live
 * preview of the current label, destination, mode and who last changed it.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Loader2, Rocket, ShoppingBag, Eye, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LandingSettingsPage({ session }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { "x-user-role": session?.user?.role || "admin", "x-user-id": session?.user?.id || "" };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/landing-cta`, { headers });
      setCfg(r.data);
    } catch { toast.error("Failed to load landing settings"); }
  }, [JSON.stringify(headers)]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setMode = async (mode) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/admin/landing-cta`, { mode }, { headers });
      setCfg(r.data);
      toast.success(`Primary CTA set to ${mode === "founding_partner" ? "Founding Partner" : "Standard"} mode`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };

  if (!cfg) return <div className="py-16 text-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const Option = ({ mode, icon: Icon, title, desc, label }) => {
    const active = cfg.mode === mode;
    return (
      <button
        type="button"
        onClick={() => !active && setMode(mode)}
        disabled={busy}
        className={`text-left rounded-xl border-2 p-5 transition-colors ${active ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e5e7eb] bg-white hover:border-[#cbd5e1]"}`}
        data-testid={`landing-cta-option-${mode}`}
      >
        <div className="flex items-center justify-between">
          <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${active ? "bg-[#2563eb] text-white" : "bg-[#f3f4f6] text-[#374151]"}`}><Icon className="h-5 w-5" /></div>
          {active && <span className="text-[11.5px] font-bold uppercase tracking-wide text-[#2563eb]">Selected</span>}
        </div>
        <div className="mt-3 font-bold text-[#111827]">{title}</div>
        <div className="mt-1 text-[13px] text-[#6b7280]">{desc}</div>
        <div className="mt-3 inline-flex items-center rounded-md bg-[#0b1220] px-3 py-1.5 text-[12px] font-semibold text-white">{label}</div>
      </button>
    );
  };

  return (
    <div className="max-w-4xl" data-testid="landing-settings-page">
      <h1 className="text-[26px] font-bold text-[#0f172a]">Landing Page</h1>
      <p className="mt-1 text-[13px] text-[#64748b]">Choose what the public "Get Started" button does. During the Founding Partner period, use Founding Partner mode so visitors go straight to no-payment onboarding.</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Option mode="founding_partner" icon={Rocket} title="Founding Partner Get Started" desc="Routes visitors to the Founding Partner direct onboarding. No pricing, quote, PO, payment or admin approval." label="Get Started as a Founding Partner" />
        <Option mode="standard" icon={ShoppingBag} title="Standard Get Started" desc="Routes visitors to the standard commercial onboarding, including normal pricing / payment / PO logic." label="Get Started" />
      </div>

      <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="landing-cta-preview">
        <div className="flex items-center gap-2 text-[#0f172a]"><Eye className="h-4 w-4" /><h2 className="text-base font-bold">Current CTA Preview</h2></div>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[13px]">
          <div className="flex justify-between border-b border-[#f0f1f3] py-1.5"><dt className="text-[#6b7280]">Current Mode</dt><dd className="font-semibold" data-testid="landing-cta-current-mode">{cfg.mode === "founding_partner" ? "Founding Partner" : "Standard"}</dd></div>
          <div className="flex justify-between border-b border-[#f0f1f3] py-1.5"><dt className="text-[#6b7280]">Current Label</dt><dd className="font-semibold">{cfg.label}</dd></div>
          <div className="flex justify-between border-b border-[#f0f1f3] py-1.5"><dt className="text-[#6b7280]">Destination</dt><dd className="font-mono text-[12px]">{cfg.destination}</dd></div>
          <div className="flex justify-between border-b border-[#f0f1f3] py-1.5"><dt className="text-[#6b7280]">Destination Type</dt><dd>{cfg.destination_label}</dd></div>
          <div className="flex justify-between border-b border-[#f0f1f3] py-1.5"><dt className="text-[#6b7280]">Last Changed By</dt><dd>{cfg.updated_by_name || cfg.updated_by || "—"}</dd></div>
          <div className="flex justify-between border-b border-[#f0f1f3] py-1.5"><dt className="text-[#6b7280]">Last Changed At</dt><dd>{cfg.updated_at ? new Date(cfg.updated_at).toLocaleString() : "—"}</dd></div>
        </dl>
        <a href="/myschoolrx" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#2563eb] hover:underline" data-testid="landing-cta-open-landing">
          Open the landing page <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}
